import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDeclarations, fetchDebitNotes, downloadDebitNote } from '../services/api';

export default function DossierPage() {
  const [dossier, setDossier] = useState<any>(null);
  const [debitNotes, setDebitNotes] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([
      fetchDeclarations().then(decs => {
        const arr = Array.isArray(decs) ? decs : [];
        setDossier(arr[0] || null);
      }),
      fetchDebitNotes().then(setDebitNotes).catch(() => {}),
    ]).then(results => {
      if (results[0].status === 'rejected') setError((results[0] as PromiseRejectedResult).reason?.message || 'Erreur');
      setLoading(false);
    });
  }, []);

  const handleFileUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('declaration_id', dossier?.id || '');
      const token = localStorage.getItem('auth_token');
      const base = import.meta.env.VITE_API_BASE_URL || '';
      const declId = dossier?.id || '';
      const res = await fetch(`${base}/api/v1/fpt/declarations/${declId}/justification`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) {
        setUploadMsg('Justification envoyée');
      } else {
        setUploadMsg('Envoi non disponible pour le moment');
      }
    } catch {
      setUploadMsg('Envoi non disponible pour le moment');
    } finally {
      setUploading(false);
    }
  };

  const matchingDebitNote = debitNotes.find(dn => dn.declaration_id === dossier?.id);

  if (loading) return <div className="loading">Chargement...</div>;
  if (error) return <div className="error-msg">{error}</div>;
  if (!dossier) return <div className="card"><p>Aucune déclaration en cours.</p></div>;

  const estimation = dossier.system_amount ?? dossier.estimation_usd ?? 0;
  const declared = dossier.declared_amount ?? dossier.declaration_usd ?? dossier.declared_revenue_usd ?? estimation;
  const variance = dossier.delta_percent ?? dossier.variance_pct ?? (estimation > 0 ? Math.round(((declared - estimation) / estimation) * 10000) / 100 : null);
  const taxDue = (declared || estimation) * (dossier.fpt_rate || 0.04);

  return (
    <div className="card">
      <div className="head">
        <div>
          <h3>Mon dossier FPT</h3>
          <div className="sub">{dossier.period || ''} — {dossier.hotel_name || ''} — Réf: {dossier.declaration_reference || ''}</div>
        </div>
        <span className={`badge ${dossier.status === 'VALIDATED' ? 'green' : 'orange'}`}>{dossier.status || 'EN COURS'}</span>
      </div>

      <div className="grid4" style={{ marginTop: 18 }}>
        <div className="info">
          <small>Estimation système</small>
          <strong>{Number(estimation).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</strong>
          {Number(dossier.collected_cdf || 0) > 0 && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
              {Number(dossier.collected_usd || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD + {Number(dossier.collected_cdf).toLocaleString('fr-FR')} CDF
            </div>
          )}
        </div>
        <div className="info">
          <small>Déclaration</small>
          <strong>{Number(declared).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</strong>
        </div>
        <div className="info">
          <small>Écart</small>
          <strong>{variance != null ? `${variance} %` : '–'}</strong>
        </div>
        <div className="info">
          <small>FPT dû (4%)</small>
          <strong>{taxDue.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</strong>
          {Number(dossier.collected_cdf || 0) > 0 && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
              ({(Number(dossier.collected_usd || 0) * (dossier.fpt_rate || 0.04)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD + {Math.round(Number(dossier.collected_cdf) * (dossier.fpt_rate || 0.04)).toLocaleString('fr-FR')} CDF)
            </div>
          )}
        </div>
      </div>

      {variance != null && Math.abs(Number(variance)) > 5 && (
        <div className="notice" style={{ marginTop: 18 }}>
          L'écart est journalisé et transmis au circuit administratif interne. Aucun onglet de contrôle interne n'est visible dans le portail Assujetti.
        </div>
      )}

      {uploadMsg && (
        <div className="notice" style={{ marginTop: 12, background: '#eaf8f1', borderColor: '#a3dfc4', color: 'var(--green)' }}>{uploadMsg}</div>
      )}

      <div className="actions">
        <button className="btn primary" onClick={() => navigate('/payments')}>Payer maintenant</button>
        <button
          className="btn"
          onClick={() => matchingDebitNote ? downloadDebitNote(matchingDebitNote.id) : alert('Aucune note de débit disponible')}
        >
          Télécharger note de débit
        </button>
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? 'Envoi...' : 'Joindre justification'}
        </button>
        <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} accept=".pdf,.jpg,.png" />
      </div>
    </div>
  );
}
